import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Separator } from '../../ui/separator';
import { CheckCircle2, XCircle, ExternalLink } from 'lucide-react';

interface HelpModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HelpModal({ open, onOpenChange }: HelpModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!w-[calc(100%-2rem)] !max-w-[900px] max-h-[90vh] !top-[calc(50%-16px)] flex flex-col overflow-hidden">
        <DialogHeader className="sticky top-0 bg-background z-10 pb-4 border-b shrink-0">
          <DialogTitle className="text-2xl">Financial Governance Help & Guidance</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-8 py-4">
            {/* Section B: CapEx & R&D Guidelines (Side-by-Side Table) */}
            <section>
              <h3 className="text-xl font-semibold mb-4">CapEx & R&D Guidelines</h3>
              
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="w-1/2 p-4 text-left font-semibold border-r">
                        CapEx (Capital Expenditure)
                      </th>
                      <th className="w-1/2 p-4 text-left font-semibold">
                        R&D (Research & Development)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-4 align-top border-r border-t">
                        <h4 className="font-semibold mb-3 text-sm">What Qualifies:</h4>
                        <ul className="space-y-2 text-sm">
                          <li className="flex items-start gap-2">
                            <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                            <span>Work that adds value to the business (either internally or externally)</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                            <span>Activities that create future money-making or cost-saving assets</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                            <span>Products we sell commercially AND internal tools/systems that deliver business value (e.g., BI tooling, reporting, operational platforms)</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                            <span>Work that adds longevity to existing assets (e.g., addressing strategic technical debt)</span>
                          </li>
                        </ul>
                      </td>
                      <td className="p-4 align-top border-t">
                        <h4 className="font-semibold mb-3 text-sm">What Qualifies:</h4>
                        <ul className="space-y-2 text-sm">
                          <li className="flex items-start gap-2">
                            <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                            <span>Activities involving technical uncertainty that is new to us</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                            <span>Work where the final outcome was unclear at project start due to:</span>
                          </li>
                          <li className="ml-6 flex items-start gap-2">
                            <span className="text-green-600 dark:text-green-400 mt-0.5">◦</span>
                            <span>Multiple technical options/change of approach required</span>
                          </li>
                          <li className="ml-6 flex items-start gap-2">
                            <span className="text-green-600 dark:text-green-400 mt-0.5">◦</span>
                            <span>Delays caused by technical issues</span>
                          </li>
                          <li className="ml-6 flex items-start gap-2">
                            <span className="text-green-600 dark:text-green-400 mt-0.5">◦</span>
                            <span>Technical uncertainty or complexity</span>
                          </li>
                          <li className="ml-6 flex items-start gap-2">
                            <span className="text-green-600 dark:text-green-400 mt-0.5">◦</span>
                            <span>Limited information available in the public domain</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                            <span>Must aim to achieve an advance in science or technology</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                            <span>Examples include: proof of concept development, research of unproven technologies, design development and testing</span>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <Separator />

            {/* Section C: Examples (Side-by-Side Cards) */}
            <section>
              <h3 className="text-xl font-semibold mb-4">Examples</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* CapEx Examples */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">CapEx Examples</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2 text-green-700 dark:text-green-400">
                        <CheckCircle2 className="h-5 w-5" />
                        Qualifies
                      </h4>
                      <ul className="space-y-2 ml-7">
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                          <span>Building a new customer-facing feature in a commercial product</span>
                        </li>
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                          <span>Developing internal BI dashboards that improve business operations</span>
                        </li>
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                          <span>Creating a new integration that adds business capability</span>
                        </li>
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                          <span>Major system enhancements adding significant functionality</span>
                        </li>
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                          <span>Refactoring code to extend the lifespan of a system (strategic technical debt)</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2 text-red-700 dark:text-red-400">
                        <XCircle className="h-5 w-5" />
                        Does NOT Qualify
                      </h4>
                      <ul className="space-y-2 ml-7">
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-red-600 dark:text-red-400 mt-0.5">•</span>
                          <span>Bug fixes and routine maintenance</span>
                        </li>
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-red-600 dark:text-red-400 mt-0.5">•</span>
                          <span>Training and documentation</span>
                        </li>
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-red-600 dark:text-red-400 mt-0.5">•</span>
                          <span>Day-to-day support activities</span>
                        </li>
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-red-600 dark:text-red-400 mt-0.5">•</span>
                          <span>Minor configuration changes</span>
                        </li>
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-red-600 dark:text-red-400 mt-0.5">•</span>
                          <span>Cosmetic UI updates without functional changes</span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                {/* R&D Examples */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">R&D Examples</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2 text-green-700 dark:text-green-400">
                        <CheckCircle2 className="h-5 w-5" />
                        Qualifies
                      </h4>
                      <ul className="space-y-2 ml-7">
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                          <span>Proof of concept for a novel approach where outcome was uncertain</span>
                        </li>
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                          <span>Building a custom algorithm where no existing solution met requirements</span>
                        </li>
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                          <span>Complex integrations requiring technical problem-solving beyond known patterns</span>
                        </li>
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                          <span>Performance optimization requiring research and experimentation</span>
                        </li>
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-green-600 dark:text-green-400 mt-0.5">•</span>
                          <span>Implementing emerging technologies with limited documentation</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2 text-red-700 dark:text-red-400">
                        <XCircle className="h-5 w-5" />
                        Does NOT Qualify
                      </h4>
                      <ul className="space-y-2 ml-7">
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-red-600 dark:text-red-400 mt-0.5">•</span>
                          <span>Standard CRUD operations using established patterns</span>
                        </li>
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-red-600 dark:text-red-400 mt-0.5">•</span>
                          <span>Routine bug fixing</span>
                        </li>
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-red-600 dark:text-red-400 mt-0.5">•</span>
                          <span>Implementing well-documented third-party solutions</span>
                        </li>
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-red-600 dark:text-red-400 mt-0.5">•</span>
                          <span>Straightforward UI development</span>
                        </li>
                        <li className="text-sm flex items-start gap-2">
                          <span className="text-red-600 dark:text-red-400 mt-0.5">•</span>
                          <span>Work where solution approach was clear from the start</span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>

            <Separator />

            {/* Section D: Supporting Information */}
            <section>
              <h3 className="text-xl font-semibold mb-4">Supporting Information</h3>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <ExternalLink className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm mb-2">
                        For detailed documentation and training materials, visit:
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                      >
                        <a
                          href="https://accessukltd.sharepoint.com/teams/ProductDevelopment/Shared%20Documents/Forms/AllItems.aspx?csf=1&web=1&e=10qw0O&CID=8a40737d%2Dbe46%2D4045%2Db53e%2Dfb0e448c40de&FolderCTID=0x0120005503B97F5A843146A7F6248EFE2F1C05&id=%2Fteams%2FProductDevelopment%2FShared%20Documents%2FGeneral%2FBrunch%20%27n%20Learn%20Sessions%2F2025%2027%20Feb%20Demystifying%20Financial%20Governance%20%28CapEx%20and%20R%26D%20Audits%29&viewid=e884238b%2Dac7f%2D4bac%2Dad00%2D2a60f3714059"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2"
                        >
                          View Detailed Documentation
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>
          </div>
      </DialogContent>
    </Dialog>
  );
}
